<template>
  <div class="min-h-screen bg-[#f0f2f5]" @mouseover="handleUserEvent" @keydown="handleUserEvent">
    <a-config-provider
      :locale="locale === 'en' ? enUS : jaJP"
      :form="{
        validateMessages: {
          required: $t('MSG_00_21')
        }
      }"
    >
      <a-style-provider hash-priority="high">
        <router-view></router-view>
      </a-style-provider>
    </a-config-provider>
  </div>
</template>

<script setup lang="ts">
import enUS from 'ant-design-vue/es/locale/en_US';
import jaJP from 'ant-design-vue/es/locale/ja_JP';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/ja';
import {useI18n} from 'vue-i18n';
import {useAuthStore} from '@/stores';
import {onBeforeUnmount, onMounted, provide, watch} from 'vue';
import debounce from 'lodash/debounce';
import {IMPORTANCE} from './constants';
import {jwtDecode} from 'jwt-decode';
import {Modal} from 'ant-design-vue';
import {router} from '@/router';
import {storeToRefs} from 'pinia';
import {setApiDisabled} from '@/services/http';
import {formatModalTitle} from './utils/modules/message';

const { locale, t } = useI18n();
const authStore = useAuthStore();
const { isAdmin, isDischarger, isTransporter } = storeToRefs(authStore);

// Function to insert the chat script
const insertChatScript = () => {
  // Check if the script already exists
  if (document.getElementById('chatplus-script')) {
    console.log('Chat script already inserted.');
    return;
  }

  const d: any = document;
  const s = 'https://app.chatplus.jp/cp.js';

  d['__cp_d'] = 'https://app.chatplus.jp';
  d['__cp_c'] = import.meta.env.VITE_CHATPLUS_CP_C;

  const script = d.createElement('script');
  script.async = true;
  script.src = s;
  script.id = 'chatplus-script'; // Assign a unique ID

  // Append the script to the head
  d.head.appendChild(script);

  // add style dislay block to <chat> element
  const chatElement = document.querySelector('chat');
  if (chatElement) {
    chatElement.style.display = 'block';
  }

  console.log('Chat script inserted.');
};

// Function to remove the chat script
const removeChatScript = () => {
  const script = document.getElementById('chatplus-script');
  if (script) {
    script.parentNode?.removeChild(script);
    // add style dislay none to <chat> element
    const chatElement = document.querySelector('chat');
    if (chatElement) {
      chatElement.style.display = 'none';
    }
    console.log('Chat script removed.');
  } else {
    console.log('Chat script not found.');
  }
};

// Function to handle token changes
const handleToken = (token: string | null) => {
  if (token && isDischarger.value && !isAdmin.value) {
    try {
      const decoded: any = jwtDecode(token);
      if (decoded.exp * 1000 > Date.now()) {
        insertChatScript();
        return;
      }
    } catch (error) {
      console.error('Invalid token:', error);
    }
  }
  // If token is invalid, expired, or null, remove the script
  removeChatScript();
};

// Watch for changes in the token
const stopWatch = watch(
  () => authStore.token,
  (newToken, oldToken) => {
    handleToken(newToken);
  },
  { immediate: true }
);

// Optional: Handle token on initial load if needed
onMounted(() => {
  handleToken(authStore.token);
});

// Clean up watcher on unmount
onBeforeUnmount(() => {
  stopWatch();
});
const { getAuthInfo } = useAuthStore();
getAuthInfo();

if (localStorage.getItem('language')) {
  const language = localStorage.getItem('language');
  if (language) {
    locale.value = language;
    dayjs.locale(language);
  }
}

const handleUserEvent = debounce(() => {
  localStorage.setItem('last-act-time', String(Date.now()));
}, 300);

const checkTokenExpired = () => {
  let timeServerDrift = 50 * 1000; // 50 sec
  let tokenLifeTime = Number(localStorage.getItem('token-lifetime'));
  return tokenLifeTime > 30*60*1000 - timeServerDrift;
};

const updateSyncTokenParam = () => {
  let tokenStartTime = Number(localStorage.getItem('token-starttime'));
  localStorage.setItem('token-lifetime', String(Date.now() - tokenStartTime));
};

const checkRefreshToken = async () => {
  if(document.querySelector('.timeout-dlg') || !localStorage.getItem('token'))  return;
  updateSyncTokenParam();
  if (checkTokenExpired()) {
    localStorage.clear();
    localStorage.setItem("token",'')
    setTimeout(() => {
      localStorage.setItem("timeout", String(Date.now()));
    }, 1000)
    //authStore.clearCurrentUser();
    setApiDisabled(true);
    let modal = Modal.error({
      title: t('your_session_has_expired'),
      class: 'timeout-dlg',
      okText: 'OK',
      centered: true,
      maskClosable: false,
      onOk: () => {
        modal.destroy();
        localStorage.setItem("reload", String(Date.now()));
        window.location.replace(import.meta.env.VITE_BASE_URL + 'login');
      }
    });
    const title = t('your_session_has_expired');
    modal.update({
      title: formatModalTitle(title)
    });
    return;
  }
  if(Number(localStorage.getItem("token-lifetime")) < import.meta.env.VITE_REFRESH_TOKEN_TIME){
    return
  }
  if(
    !document.hidden ||
    (localStorage.getItem('last-act-time') &&
        (Date.now() - Number(<string>localStorage.getItem('last-act-time'))) < import.meta.env.VITE_REFRESH_TOKEN_TIME)
  )
    try {
      await authStore.refreshToken();
    } catch (error) {
      console.error('Error refreshing token:', error);
    }
};
window.addEventListener('storage', (event) => {
  let keySync = ['token', 'last-act-time','token-lifetime','reload','timeout'];
  if (event.storageArea !== localStorage || !event.key || !keySync.includes(event.key)) return;
  let newVal = event.newValue ? event.newValue : '';
  switch (event.key) {
    case 'reload':
      if(document.hidden){
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') {
            location.reload();
          }
        });
      }else{
        location.reload();
      }
      break
    case 'timeout':
      if(document.querySelector('.timeout-dlg')) return;
      //authStore.clearCurrentUser();
      localStorage.clear();
      setApiDisabled(true);
      let modal = Modal.error({
        title: t('your_session_has_expired'),
        okText: 'OK',
        centered: true,
        maskClosable: false,
        class: 'timeout-dlg',
        onOk: () => {
          modal.destroy();
          localStorage.setItem("reload", String(Date.now()));
          window.location.replace(import.meta.env.VITE_BASE_URL + 'login');
        }
      });
      const title = t('your_session_has_expired');
      modal.update({
        title: formatModalTitle(title)
      });
      break
    default:
      if (localStorage.getItem(event.key) !== newVal) {
        localStorage.setItem(event.key, newVal);
      }
      break;
  }
});

window.setInterval(() => {
  try {
    checkRefreshToken();
  } catch (error) {
    console.error('Error in checkRefreshToken:', error);
  }
}, 10000); // 10 seconds

const getImportantClass = (important: String) => {
  if (important == IMPORTANCE.MOST_IMPORTANT) {
    return 'red';
  }
  if (important == IMPORTANCE.IMPORTANT) {
    return 'yellow';
  }
  if (important == IMPORTANCE.SLIGHTLY_IMPORTANT) {
    return 'blue';
  }
  return '';
};

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    localStorage.setItem('last-act-time', String(Date.now()));
  }
});

const formatNumber = (value: number): number => {
  const strValue = value.toString();

  // Handle numbers with decimal point
  if (strValue.includes('.')) {
    const [intPart, decPart] = strValue.split('.');
    // Limit integer part to 5 digits and decimal part to 2 digits
    const limitedIntPart = intPart.slice(0, 5);
    const formattedDecPart = decPart.slice(0, 2).padEnd(2, '0');
    return Number(`${limitedIntPart}.${formattedDecPart}`);
  }

  // Handle whole numbers
  const digits = strValue.replace('.', '');

  if (digits.length < 5) {
    return Number(digits + '.00');
  }

  const integerPart = digits.slice(0, 5);
  const decimalPart = digits.slice(5, 7).padEnd(2, '0');
  return Number(`${integerPart}.${decimalPart}`);
};

const validateNumber = (event: KeyboardEvent) => {
  const target = event.target as HTMLInputElement;
  const value = target.value;
  const key = event.key;
  const cursorPosition = target.selectionStart || 0;
  const selectionEnd = target.selectionEnd || cursorPosition;

  // Allow backspace, delete, arrow keys etc
  if (
    event.metaKey ||
    event.ctrlKey ||
    key === 'Backspace' ||
    key === 'Delete' ||
    key === 'ArrowLeft' ||
    key === 'ArrowRight' ||
    key === 'Tab'
  ) {
    return;
  }

  // Only allow numbers and dot
  if (!/^\d|\./.test(key)) {
    event.preventDefault();
    return;
  }

  // Calculate the value after the potential change
  const newValue = value.substring(0, cursorPosition) + key + value.substring(selectionEnd);

  const parts = newValue.split('.');
  const integerPart = parts[0];
  const decimalPart = parts[1] || '';

  // If no decimal point exists
  if (!newValue.includes('.')) {
    if (integerPart.length > 7) {
      event.preventDefault();
      return;
    }
  }
  // If decimal point exists
  else {
    // If cursor is before decimal point
    if (cursorPosition <= newValue.indexOf('.')) {
      if (integerPart.length > 5) {
        event.preventDefault();
        return;
      }
    }
    // If cursor is after decimal point
    else if (decimalPart.length > 2) {
      event.preventDefault();
      return;
    }
  }
};

provide('getImportantClass', getImportantClass);
provide('formatNumber', formatNumber);
provide('validateNumber', validateNumber);
</script>
